// ─────────────────────────────────────────────────────────────────────────
// Shared invoice/receipt PDF templates for the Client Portal.
//
// Mirrors the official templates used internally in Finance.js (the same
// template_style/primary_color/logo/bank/QR/footer fields a firm configures
// in Settings → Billing) so a client downloading their own invoice sees the
// exact same document a staff member would print — not a simplified one.
// ─────────────────────────────────────────────────────────────────────────

export function buildUpiQrUrl(upiId, payeeName, amount, note) {
  if (!upiId) return '';
  const params = new URLSearchParams({
    pa: upiId,
    pn: payeeName || '',
    am: String(amount || ''),
    cu: 'INR',
    tn: note || '',
  });
  const upiString = `upi://pay?${params.toString()}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(upiString)}`;
}

export function numberToWords(num) {
  if (!num || isNaN(num)) return '';
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  };
  const parts = String(num).split('.');
  return inWords(parseInt(parts[0])) + (parts[1] && parseInt(parts[1]) > 0 ? ' and ' + inWords(parseInt(parts[1])) + ' Paise' : '');
}

export function buildInvoiceHtml(invoice) {
  const tpl = invoice.template_style || 'classic';
  const pColor = invoice.primary_color || '#0f1f45';
  const font = invoice.invoice_font || 'Plus Jakarta Sans';
  const isCorp = tpl === 'corporate';
  const isBold = tpl === 'bold';
  const theadBg = pColor;
  const headerBg = isCorp ? '#ffffff' : pColor;
  const headerText = isCorp ? pColor : '#ffffff';
  const showScope = invoice.show_scope_of_work !== false;
  const showDiscount = invoice.show_discount_column !== false;
  const showLedger = invoice.show_ledger_balance;
  const showWatermark = invoice.show_watermark;
  const watermarkText = invoice.watermark_text || 'PAID';

  const gFontsLink = ['Plus Jakarta Sans', 'Poppins', 'Montserrat', 'Inter'].some(f => font.includes(f))
    ? `<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">`
    : '';

  const separator = isCorp
    ? `<div style="height:2px;background:${pColor};"></div>`
    : isBold
    ? `<div style="height:4px;background:#f97316;"></div>`
    : `<div style="display:flex;height:5px;"><div style="flex:1;background:${pColor}"></div><div style="flex:1;background:#c8a951"></div><div style="flex:1;background:#e8d9a0"></div></div>`;

  const isGst = invoice.gst_type !== 'none' && Number(invoice.total_gst) > 0;
  const docTitle = invoice.invoice_heading || (isGst ? 'TAX INVOICE' : 'INVOICE');
  const showSac = invoice.show_sac_code !== false;

  const amountForQr = Number(invoice.balance_due) > 0 ? invoice.balance_due : invoice.total_amount;
  const qrUrl = (invoice.upi_id && invoice.show_invoice_qr !== false)
    ? buildUpiQrUrl(invoice.upi_id, invoice.firm_name || 'ChanakyaX', amountForQr, 'Invoice ' + invoice.invoice_number)
    : '';

  return '<!DOCTYPE html><html><head><title>Invoice ' + invoice.invoice_number + '</title>'
    + gFontsLink
    + '<style>'
    + 'body{font-family:"' + font + '",Arial,sans-serif;margin:0;padding:0;color:#1a1a1a;font-size:12px}'
    + '.page{padding:24px;position:relative}'
    + '.inv-header{display:flex;justify-content:space-between;align-items:flex-start;background:' + headerBg + ';padding:20px 24px;' + (isCorp ? 'border-bottom:2.5px solid ' + pColor + ';' : '') + '}'
    + '.firm-name{font-size:18px;font-weight:bold;color:' + headerText + ';letter-spacing:0.3px}'
    + '.firm-sub{font-size:10px;color:' + headerText + ';opacity:' + (isCorp ? '0.7' : '0.85') + ';margin-top:3px;line-height:1.7}'
    + '.doc-title{font-size:18px;font-weight:bold;color:' + headerText + ';letter-spacing:1.5px;text-align:right}'
    + '.doc-badge{font-size:8px;border:1px solid ' + (isCorp ? pColor : 'rgba(255,255,255,0.5)') + ';color:' + headerText + ';padding:2px 8px;display:inline-block;margin-top:4px;border-radius:2px;letter-spacing:0.3px}'
    + '.doc-meta{text-align:right;font-size:10px;color:' + headerText + ';opacity:' + (isCorp ? '1' : '0.9') + ';line-height:1.9;margin-top:8px}'
    + '.bill-section{display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:14px 24px;border-bottom:1px solid #eee}'
    + '.bill-badge{font-size:9px;font-weight:bold;background:' + pColor + ';color:#fff;padding:2px 8px;display:inline-block;border-radius:2px;margin-bottom:7px;letter-spacing:0.4px}'
    + '.bill-name{font-size:13px;font-weight:bold;color:#1a1a1a}'
    + '.bill-addr{font-size:10px;color:#555;line-height:1.7;margin-top:2px}'
    + '.gst-label{font-size:9px;font-weight:bold;color:#888;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:7px}'
    + '.gst-row{font-size:10px;color:#555;line-height:1.9}'
    + 'table{width:100%;border-collapse:collapse}'
    + 'thead tr{background:' + theadBg + ';color:#fff}'
    + 'th{padding:8px 10px;text-align:left;font-size:10px;font-weight:600}'
    + 'th.r{text-align:right}'
    + 'td{padding:9px 10px;border-bottom:1px solid #eee;font-size:11px;vertical-align:top}'
    + 'td.r{text-align:right}'
    + 'td.c{text-align:center}'
    + '.item-name{font-weight:bold;color:' + pColor + ';font-size:11.5px}'
    + '.item-scope{font-size:9.5px;color:#555;line-height:1.6;border-top:1px dashed #ddd;padding-top:4px;margin-top:3px}'
    + '.totals-wrap{display:grid;grid-template-columns:1fr 1fr;gap:0;padding:12px 24px 0;border-top:1px solid #eee}'
    + '.bank-block{padding-right:14px;border-right:1px solid #eee;font-size:10px}'
    + '.bank-title{font-weight:bold;color:' + pColor + ';font-size:9px;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px}'
    + '.bank-row{line-height:1.9;color:#444}'
    + '.bank-label{color:#888;display:inline-block;width:58px}'
    + '.totals-block{padding-left:14px}'
    + '.t-row{display:flex;justify-content:space-between;padding:5px 10px;border-bottom:0.5px solid #eee;font-size:11px}'
    + '.t-total{display:flex;justify-content:space-between;padding:8px 10px;background:' + theadBg + ';color:#fff;font-weight:bold;font-size:13px}'
    + '.t-ledger-row{display:flex;justify-content:space-between;padding:5px 10px;border-bottom:0.5px solid #eee;font-size:10px}'
    + '.words-bar{margin:10px 24px 0;padding:7px 10px;border-radius:4px;font-size:10px;background:#eef1f8;border-left:3px solid ' + pColor + '}'
    + '.footer-section{margin:10px 24px 0;padding:10px 0 20px;border-top:1px solid #eee;display:grid;grid-template-columns:1.4fr 1fr;gap:14px}'
    + '.tc-text{font-size:9px;color:#555;line-height:1.8}'
    + '.sig-block{text-align:center;font-size:10px}'
    + '.watermark{position:fixed;top:40%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:80px;font-weight:bold;color:rgba(0,0,0,0.05);pointer-events:none;text-transform:uppercase;letter-spacing:8px;white-space:nowrap;z-index:0}'
    + '*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}'
    + '@media print{@page{size:A4;margin:10mm}}'
    + '</style></head><body><div class="page">'

    + (showWatermark ? '<div class="watermark">' + watermarkText + '</div>' : '')

    + '<div class="inv-header" style="' + (isCorp ? 'border-left:5px solid ' + pColor + ';' : '') + '">'
    + '<div style="display:flex;flex-direction:column;gap:10px;max-width:55%">'
    + (invoice.logo_url ? '<div style="width:200px;height:64px;overflow:hidden;position:relative"><img src="' + invoice.logo_url + '" style="position:absolute;left:50%;top:50%;max-width:180px;max-height:52px;width:auto;height:auto;object-fit:contain;transform:translate(-50%,-50%) translate(' + (invoice.logo_x || 0) + 'px,' + (invoice.logo_y || 0) + 'px) scale(' + (invoice.logo_scale || 1) + ')"/></div>' : '<div style="display:inline-flex;align-items:center;justify-content:center;height:40px;padding:6px 14px;background:' + (isCorp ? pColor + '18' : 'rgba(255,255,255,0.15)') + ';border:1.5px solid ' + (isCorp ? pColor + '30' : 'rgba(255,255,255,0.3)') + ';border-radius:4px;align-self:flex-start"><span style="color:' + (isCorp ? pColor : '#fff') + ';font-size:12px;font-weight:bold;letter-spacing:0.5px">' + (invoice.firm_name || 'LCCA') + '</span></div>')
    + '<div><div class="firm-name">' + (invoice.firm_name || '') + '</div>'
    + '<div class="firm-sub">' + (invoice.firm_full_address || '') + (invoice.firm_city ? ', ' + invoice.firm_city : '') + (invoice.firm_pincode ? ' - ' + invoice.firm_pincode : '') + '<br/>'
    + 'Mobile: ' + (invoice.firm_phone_num || '') + ' | ' + (invoice.firm_email || '')
    + (invoice.firm_website ? ' | ' + invoice.firm_website : '')
    + '</div></div></div>'
    + '<div><div class="doc-title">' + docTitle + '</div>'
    + '<div class="doc-badge">ORIGINAL FOR RECIPIENT</div>'
    + '<div class="doc-meta"><span style="opacity:0.7">Invoice No. </span><strong>' + invoice.invoice_number + '</strong><br/>'
    + '<span style="opacity:0.7">Date </span><strong>' + new Date(invoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + '</strong>'
    + (invoice.due_date ? '<br/><span style="opacity:0.7">Due Date </span><strong>' + new Date(invoice.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + '</strong>' : '')
    + '</div></div></div>'

    + separator

    + '<div class="bill-section">'
    + '<div><div class="bill-badge">BILL TO</div>'
    + '<div class="bill-name">' + invoice.client_name + '</div>'
    + '<div class="bill-addr">' + (invoice.client_address || '') + (invoice.client_city ? '<br/>' + invoice.client_city : '') + (invoice.client_state ? ', ' + invoice.client_state : '') + (invoice.client_pincode ? ' - ' + invoice.client_pincode : '') + (invoice.client_mobile ? '<br/>Mobile: ' + invoice.client_mobile : '') + (invoice.client_gstin ? '<br/>GSTIN: ' + invoice.client_gstin : '') + '</div></div>'
    + (invoice.firm_gstin || invoice.firm_pan || invoice.firm_state
        ? '<div><div class="gst-label">Firm Details</div><div class="gst-row">'
          + (invoice.firm_gstin ? '<span style="color:#888">GSTIN: </span><strong>' + invoice.firm_gstin + '</strong><br/>' : '')
          + (invoice.firm_pan ? '<span style="color:#888">PAN: </span><strong>' + invoice.firm_pan + '</strong><br/>' : '')
          + (invoice.firm_state ? '<span style="color:#888">State: </span><strong>' + invoice.firm_state + '</strong>' : '')
          + '</div></div>'
        : '<div></div>')
    + '</div>'

    + '<div style="padding:0 24px"><table><thead><tr>'
    + '<th style="width:28px;text-align:center">#</th><th>Description of Service</th>'
    + (showSac ? '<th style="width:60px">SAC</th>' : '')
    + '<th class="r" style="width:70px">Rate (₹)</th>'
    + (showDiscount ? '<th class="r" style="width:60px">Discount</th>' : '')
    + (isGst ? '<th class="r" style="width:44px">GST%</th>' : '')
    + '<th class="r" style="width:70px">Amount (₹)</th>'
    + '</tr></thead><tbody>'
    + (invoice.items || []).map(function (item, i) {
        const scopeHtml = showScope && item.description
          ? '<div class="item-scope">' + item.description.replace(/\n/g, '<br/>') + '</div>'
          : '';
        const discAmt = Number(item.discount_amount || 0);
        return '<tr style="background:' + (i % 2 === 0 ? '#f9f9f9' : '#fff') + '">'
          + '<td class="c">' + (i + 1) + '</td>'
          + '<td><div class="item-name">' + (item.particular || item.name || '') + '</div>' + scopeHtml + '</td>'
          + (showSac ? '<td class="c">' + (item.sac_code || '-') + '</td>' : '')
          + '<td class="r">' + Number(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</td>'
          + (showDiscount ? '<td class="r" style="color:#e8502a">' + (discAmt > 0 ? Number(discAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—') + '</td>' : '')
          + (isGst ? '<td class="c">18%</td>' : '')
          + '<td class="r"><strong>' + Number(item.net_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</strong></td>'
          + '</tr>';
      }).join('')
    + '</tbody></table></div>'

    + '<div class="totals-wrap">'
    + '<div class="bank-block">'
    + '<div class="bank-title">Bank Details</div>'
    + '<div class="bank-row">'
    + (invoice.bank_name ? '<div><span class="bank-label">Name:</span>' + invoice.bank_name + '</div>' : '')
    + (invoice.bank_account ? '<div><span class="bank-label">Account:</span>' + invoice.bank_account + '</div>' : '')
    + (invoice.bank_ifsc ? '<div><span class="bank-label">IFSC:</span>' + invoice.bank_ifsc + '</div>' : '')
    + (invoice.bank_branch ? '<div><span class="bank-label">Bank:</span>' + invoice.bank_branch + '</div>' : '')
    + '</div>'
    + (qrUrl ? '<div style="margin-top:8px"><div style="font-weight:bold;font-size:9px;color:#555;margin-bottom:4px">PAYMENT QR CODE</div><div style="display:flex;align-items:center;gap:8px"><img src="' + qrUrl + '" style="width:52px;height:52px;border:1px solid #ccc;border-radius:3px;padding:2px;background:#fff"/><div style="font-size:9px;color:#555;line-height:1.8"><div style="font-weight:bold;color:' + pColor + '">UPI ID</div><div>' + invoice.upi_id + '</div></div></div></div>' : '')
    + '</div>'
    + '<div class="totals-block">'
    + '<div style="border:0.5px solid #e8e8e8;border-radius:4px;overflow:hidden;margin-bottom:6px">'
    + '<div class="t-row"><span style="color:#666">Subtotal</span><span>₹' + Number(invoice.subtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>'
    + (Number(invoice.discount_amount) > 0 ? '<div class="t-row" style="color:#e8502a"><span>Discount</span><span>— ₹' + Number(invoice.discount_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>' : '')
    + (isGst && invoice.gst_type === 'cgst_sgst' ? '<div class="t-row"><span style="color:#666">CGST (9%)</span><span>₹' + Number(invoice.cgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div><div class="t-row"><span style="color:#666">SGST (9%)</span><span>₹' + Number(invoice.sgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>' : '')
    + (isGst && invoice.gst_type === 'igst' ? '<div class="t-row"><span style="color:#666">IGST (18%)</span><span>₹' + Number(invoice.igst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>' : '')
    + (Number(invoice.round_off) !== 0 ? '<div class="t-row" style="color:#999;font-size:10px"><span>Round Off</span><span>' + (Number(invoice.round_off) > 0 ? '+' : '') + Number(invoice.round_off).toFixed(2) + '</span></div>' : '')
    + '<div class="t-total"><span>Total</span><span>₹' + Number(invoice.total_amount).toLocaleString('en-IN') + '</span></div>'
    + '</div>'
    + (showLedger ? '<div style="border:0.5px solid #e8e8e8;border-radius:4px;overflow:hidden">'
      + (Number(invoice.amount_paid) > 0 ? '<div class="t-ledger-row"><span style="color:#666">Received</span><span style="color:#16a34a;font-weight:bold">₹' + Number(invoice.amount_paid).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>' : '')
      + '<div class="t-ledger-row" style="font-weight:bold"><span style="color:#dc2626">Balance Due</span><span style="color:#dc2626">₹' + Number(invoice.balance_due).toLocaleString('en-IN', { minimumFractionDigits: 2 }) + '</span></div>'
      + '</div>' : '')
    + '</div></div>'

    + (invoice.amount_in_words ? '<div class="words-bar"><span style="color:#888;font-weight:bold">Amount in Words: </span><em style="color:#333">' + invoice.amount_in_words + '</em></div>' : '')

    + '<div class="footer-section">'
    + '<div class="tc-text"><strong style="color:#333">Terms &amp; Conditions</strong><br/>' + (invoice.terms_and_conditions || '').replace(/\n/g, '<br/>') + '</div>'
    + '<div class="sig-block">'
    + (invoice.signature_url ? '<img src="' + invoice.signature_url + '" style="max-height:44px;max-width:100px;display:block;margin:0 auto 4px"/>' : '')
    + '<div style="width:100px;height:36px;border-bottom:1px solid #999;margin:0 auto 4px"></div>'
    + '<div style="font-weight:bold;font-size:11px;color:#333">' + (invoice.signature_name || 'Authorised Signature') + '</div>'
    + (invoice.signature_designation ? '<div style="color:#888;font-size:9px;margin-top:1px">' + invoice.signature_designation + '</div>' : '')
    + '<div style="color:#555;font-size:9px;margin-top:1px">For ' + (invoice.firm_name || '') + '</div>'
    + '</div></div>'

    + '<script>window.onload=function(){window.print()}<\/script>'
    + '</body></html>';
}

export function buildReceiptHtml(invoice, payment) {
  const tpl = invoice.template_style || 'classic';
  const pColor = invoice.primary_color || '#0f1f45';
  const font = invoice.invoice_font || 'Plus Jakarta Sans';
  const isCorp = tpl === 'corporate';
  const isBold = tpl === 'bold';
  const showLedger = invoice.show_receipt_ledger !== false;
  const showRemark = invoice.show_receipt_remark !== false;
  const showSettle = invoice.show_receipt_settlement !== false;

  const separator = isCorp
    ? `<div style="height:2px;background:${pColor};"></div>`
    : isBold
    ? `<div style="height:4px;background:#f97316;"></div>`
    : `<div style="display:flex;height:4px;"><div style="flex:1;background:${pColor}"></div><div style="flex:1;background:#c8a951"></div><div style="flex:1;background:#e8d9a0"></div></div>`;

  const gFonts = ['Plus Jakarta Sans', 'Poppins', 'Montserrat', 'Inter'].some(f => font.includes(f))
    ? `<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">`
    : '';

  const firmAddr = [invoice.firm_full_address, invoice.firm_city, invoice.firm_pincode].filter(Boolean).join(', ');
  const isCorpHeader = isCorp;
  const receiptNo = payment.receipt_number || '';
  const payDate = payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const balText = Number(invoice.balance_due) <= 0 ? 'NIL — Fully Paid' : '&#8377;' + Number(invoice.balance_due).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const remarkBlock = (showRemark && payment.notes)
    ? `<div style="font-size:10px;color:#555;margin-top:6px;font-style:italic">Remark: ${payment.notes}</div>`
    : '';

  const settleTable = showSettle
    ? `<div style="padding:12px 24px;border-bottom:0.5px solid #eee;">
        <div style="font-size:10px;font-weight:bold;color:#333;margin-bottom:8px;">Payment settled against following invoice(s)</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <thead>
            <tr style="background:#f4f4f4;">
              <th style="padding:7px 10px;text-align:left;font-weight:600;color:#666;border:0.5px solid #e8e8e8;">Date</th>
              <th style="padding:7px 10px;text-align:left;font-weight:600;color:#666;border:0.5px solid #e8e8e8;">Invoice No.</th>
              <th style="padding:7px 10px;text-align:right;font-weight:600;color:#666;border:0.5px solid #e8e8e8;">Invoice Amount</th>
              <th style="padding:7px 10px;text-align:right;font-weight:600;color:#666;border:0.5px solid #e8e8e8;">Payment</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding:7px 10px;border:0.5px solid #e8e8e8;">${payDate}</td>
              <td style="padding:7px 10px;border:0.5px solid #e8e8e8;font-weight:bold;color:${pColor};">${invoice.invoice_number}</td>
              <td style="padding:7px 10px;text-align:right;border:0.5px solid #e8e8e8;">&#8377;${Number(invoice.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td style="padding:7px 10px;text-align:right;border:0.5px solid #e8e8e8;font-weight:bold;color:#16a34a;">&#8377;${Number(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            <tr style="background:#f9f9f9;">
              <td colspan="3" style="padding:7px 10px;border:0.5px solid #e8e8e8;font-weight:bold;text-align:right;color:#333;">Total</td>
              <td style="padding:7px 10px;text-align:right;border:0.5px solid #e8e8e8;font-weight:bold;color:${pColor};">&#8377;${Number(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>
      </div>`
    : '';

  const footerContent = invoice.receipt_footer_html
    ? `<div style="font-size:9px;color:#555;line-height:1.8;">${invoice.receipt_footer_html}</div>`
    : `<div style="font-size:9px;color:#555;line-height:1.8;">For more services, please visit our website: <strong style="color:${pColor};">${invoice.firm_website || 'www.legalchanakya.in'}</strong><br/>Access your <strong>Client Login</strong> to download returns, check ledger, track assignments and make payments — anytime, anywhere.</div>`;

  const signatureBlock = invoice.signature_url
    ? `<img src="${invoice.signature_url}" style="max-height:40px;max-width:90px;display:block;margin:0 auto 4px;"/>`
    : `<div style="width:90px;height:34px;border-bottom:1px solid #999;margin:0 auto 4px;"></div>`;

  return `<!DOCTYPE html><html><head><title>Receipt ${receiptNo}</title>
    ${gFonts}
    <style>
      @page{size:A4;margin:10mm}
      *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important;}
      body{font-family:"${font}",Arial,sans-serif;margin:0;padding:0;color:#1a1a1a;font-size:12px;}
      .page{width:100%;}
    </style>
    </head><body><div class="page">

    <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:20px 24px 16px;${isCorpHeader ? 'border-bottom:2.5px solid ' + pColor + ';border-left:5px solid ' + pColor + ';' : 'background:' + pColor + ';'}">
      <div style="display:flex;flex-direction:column;gap:8px;">
        ${invoice.logo_url
          ? `<img src="${invoice.logo_url}" style="max-width:180px;max-height:44px;width:auto;height:auto;object-fit:contain;object-position:left center;display:block;"/>`
          : `<div style="display:inline-flex;align-items:center;height:36px;padding:5px 12px;background:${isCorpHeader ? pColor + '18' : 'rgba(255,255,255,0.15)'};border:1.5px solid ${isCorpHeader ? pColor + '30' : 'rgba(255,255,255,0.3)'};border-radius:4px;align-self:flex-start;"><span style="color:${isCorpHeader ? pColor : '#fff'};font-size:10px;font-weight:bold;">${invoice.firm_name || 'LCCA'}</span></div>`}
        <div>
          <div style="font-size:14px;font-weight:bold;color:${isCorpHeader ? pColor : '#fff'};line-height:1.2;">${invoice.firm_name || 'Legal Chanakya Corporate Advisors'}</div>
          <div style="font-size:9px;color:${isCorpHeader ? '#666' : '#fff'};opacity:${isCorpHeader ? '1' : '0.85'};margin-top:3px;line-height:1.7;">
            ${firmAddr}<br/>
            Mobile: ${invoice.firm_phone_num || ''} | ${invoice.firm_email || ''}
          </div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:24px;font-weight:bold;color:${isCorpHeader ? pColor : '#fff'};letter-spacing:1px;">${invoice.receipt_heading || 'Receipt'}</div>
      </div>
    </div>

    ${separator}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:14px 24px;border-bottom:0.5px solid #eee;">
      <div>
        <div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:5px;">Received From</div>
        <div style="font-size:13px;font-weight:bold;color:#1a1a1a;">${invoice.client_name}</div>
        ${invoice.client_address ? `<div style="font-size:10px;color:#555;margin-top:2px;line-height:1.7;">${invoice.client_address}${invoice.client_city ? ', ' + invoice.client_city : ''}${invoice.client_state ? ', ' + invoice.client_state : ''}${invoice.client_pincode ? ' - ' + invoice.client_pincode : ''}</div>` : ''}
        ${remarkBlock}
      </div>
      <div style="text-align:right;">
        <div style="font-size:10px;color:#555;line-height:2;">
          <div><span style="color:#888;">Receipt No.: </span><strong style="color:#1a1a1a;">${receiptNo}</strong></div>
          <div><span style="color:#888;">Receipt Date: </span><strong style="color:#1a1a1a;">${payDate}</strong></div>
          ${payment.reference_number ? `<div><span style="color:#888;">Ref / UTR: </span><strong style="color:#1a1a1a;">${payment.reference_number}</strong></div>` : ''}
        </div>
      </div>
    </div>

    <div style="padding:0 24px;"><table style="width:100%;border-collapse:collapse;font-size:10px;">
      <tbody><tr style="background:#f9f9f9;">
        <td style="padding:10px;border-bottom:0.5px solid #eee;vertical-align:top;">
          <div style="font-weight:bold;color:${pColor};font-size:11px;">Payment received through ${payment.mode ? payment.mode.replace(/_/g, ' ') : 'Bank Transfer'}</div>
          <div style="font-size:9px;color:#666;margin-top:2px;">Payment Mode: ${payment.mode ? payment.mode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Bank Transfer'}</div>
        </td>
        <td style="padding:10px;text-align:right;border-bottom:0.5px solid #eee;font-weight:bold;font-size:11px;">&#8377;${Number(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>
      </tbody>
    </table>
    </div>

    <div style="display:grid;grid-template-columns:1fr auto;align-items:center;padding:10px 24px;border-bottom:0.5px solid #eee;">
      <div style="font-size:10px;color:#555;font-style:italic;">
        <span style="font-weight:bold;font-style:normal;color:#333;">Amount in Words: </span>${payment.amount_in_words || numberToWords(Number(payment.amount)) + ' Rupees only.'}
      </div>
      <div style="display:flex;align-items:center;gap:12px;">
        <span style="font-size:11px;color:#888;">Total:</span>
        <span style="font-size:15px;font-weight:bold;color:${pColor};">&#8377;${Number(payment.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
      </div>
    </div>

    ${settleTable}

    ${showLedger ? `
    <div style="padding:8px 24px;border-bottom:0.5px solid #eee;display:flex;justify-content:space-between;font-size:11px;">
      <span style="color:#666;">Balance after this payment:</span>
      <strong style="color:${Number(invoice.balance_due) <= 0 ? '#16a34a' : '#dc2626'};">${balText}</strong>
    </div>` : ''}

    <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:16px;padding:14px 24px 20px;">
      <div>${footerContent}</div>
      <div style="text-align:center;font-size:10px;">
        ${signatureBlock}
        <div style="font-weight:bold;font-size:11px;color:#333;">${invoice.signature_name || 'Authorised Signature'}</div>
        ${invoice.signature_designation ? `<div style="color:#888;font-size:9px;">${invoice.signature_designation}</div>` : ''}
        <div style="color:#555;font-size:9px;margin-top:1px;">For ${invoice.firm_name || ''}</div>
      </div>
    </div>

    </div>
    <script>window.onload=function(){window.print()}<\/script>
    </body></html>`;
}

export function openPrintWindow(html) {
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}
